import React, { useEffect, useMemo, useState } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { CrmButton, CrmDrawer, CrmSearchInput, CrmTooltip } from '../ui';

const moveItem = (items, itemId, destinationId) => {
  const index = items.indexOf(itemId);
  const destination = items.indexOf(destinationId);
  if (index < 0 || destination < 0) return items;
  const next = [...items];
  [next[index], next[destination]] = [next[destination], next[index]];
  return next;
};

function CrmColumnManager({
  open,
  onClose,
  columns,
  preferences,
  onApply,
  onRestoreDefaults,
  title = 'Edit columns',
  subtitle = 'Choose which properties appear and set their order.',
}) {
  const [search, setSearch] = useState('');
  const [draftOrder, setDraftOrder] = useState([]);
  const [draftHidden, setDraftHidden] = useState([]);
  const columnsById = useMemo(
    () => new Map(columns.map((column) => [column.id, column])),
    [columns]
  );

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setDraftOrder(preferences.order.length ? preferences.order : columns.map((column) => column.id));
    setDraftHidden(preferences.hidden || []);
  }, [columns, open, preferences.hidden, preferences.order]);

  const selectedColumns = draftOrder
    .map((id) => columnsById.get(id))
    .filter((column) => column && !draftHidden.includes(column.id));
  const availableColumns = columns.filter((column) => (
    draftHidden.includes(column.id) &&
    column.label.toLowerCase().includes(search.trim().toLowerCase())
  ));

  const setVisible = (column, visible) => {
    if (column.hideable === false) return;
    setDraftHidden((previous) => visible
      ? previous.filter((id) => id !== column.id)
      : [...new Set([...previous, column.id])]);
  };

  return (
    <CrmDrawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="sm"
      className="crm-column-manager"
      footer={(
        <>
          <CrmButton
            variant="tertiary"
            startIcon={<RestartAltIcon />}
            onClick={() => {
              onRestoreDefaults();
              onClose();
            }}
          >
            Restore default
          </CrmButton>
          <div className="crm-column-manager__footer-spacer" />
          <CrmButton
            variant="primary"
            onClick={() => {
              onApply({ ...preferences, order: draftOrder, hidden: draftHidden });
              onClose();
            }}
          >
            Apply
          </CrmButton>
        </>
      )}
    >
      <CrmSearchInput
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search properties"
        aria-label="Search columns"
      />

      <section className="crm-column-manager__section">
        <h3>Selected columns</h3>
        <div className="crm-column-manager__list">
          {selectedColumns.map((column, index) => {
            const locked = column.hideable === false;
            const toggle = () => { if (!locked) setVisible(column, false); };
            return (
              <div
                key={column.id}
                className={`crm-column-manager__item is-selected ${locked ? 'is-locked' : ''}`}
                role="checkbox"
                aria-checked="true"
                tabIndex={locked ? -1 : 0}
                onClick={toggle}
                onKeyDown={(event) => {
                  if (!locked && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    toggle();
                  }
                }}
              >
                <label onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked
                    disabled={locked}
                    onChange={(event) => setVisible(column, event.target.checked)}
                  />
                  <span>{column.label}</span>
                </label>
                <div className="crm-column-manager__move" onClick={(event) => event.stopPropagation()}>
                  <CrmTooltip content="Move up">
                    <button
                      type="button"
                      onClick={() => setDraftOrder((order) => moveItem(order, column.id, selectedColumns[index - 1]?.id))}
                      disabled={index === 0}
                      aria-label={`Move ${column.label} up`}
                    >
                      <ArrowUpwardIcon />
                    </button>
                  </CrmTooltip>
                  <CrmTooltip content="Move down">
                    <button
                      type="button"
                      onClick={() => setDraftOrder((order) => moveItem(order, column.id, selectedColumns[index + 1]?.id))}
                      disabled={index === selectedColumns.length - 1}
                      aria-label={`Move ${column.label} down`}
                    >
                      <ArrowDownwardIcon />
                    </button>
                  </CrmTooltip>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="crm-column-manager__section">
        <h3>Available columns</h3>
        <div className="crm-column-manager__list">
          {availableColumns.map((column) => (
            <label key={column.id} className="crm-column-manager__available">
              <input
                type="checkbox"
                checked={false}
                onChange={(event) => setVisible(column, event.target.checked)}
              />
              <span>{column.label}</span>
            </label>
          ))}
          {!availableColumns.length ? (
            <p className="crm-column-manager__empty">No hidden properties match your search.</p>
          ) : null}
        </div>
      </section>
    </CrmDrawer>
  );
}

export default CrmColumnManager;
